import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { rrulestr } from 'rrule';
import Pagination from 'components/Pagination';
import Footer from '../Footer';
import qs from 'qs';
import { currencyFormattedAmount } from 'helpers/currency';
import { currentTime } from 'helpers/time';
import * as DataList from 'components/DataList';
import { accentFor } from 'components/Tables/ScheduleList';

export const SERVICE_AGREEMENT_COLUMNS = [
  { area: 'number', label: 'Agreement #', width: '148px', bone: 'short' },
  { area: 'agreement', label: 'Agreement', width: 'minmax(200px, 1.6fr)' },
  { area: 'schedule', label: 'Schedule', width: 'minmax(170px, 1fr)', icon: 'repeat' },
  { area: 'next', label: 'Next visit', width: '128px', bone: 'short', icon: 'calendar' },
  { area: 'client', label: 'Client', width: 'minmax(190px, 1.3fr)', icon: 'map-marker' },
  { area: 'price', label: 'Price', width: '104px', bone: 'single' },
  { area: 'status', label: 'Status', width: '104px', bone: 'pill' },
  { area: 'actions', label: '', width: '32px', bone: 'none' },
];

export const SERVICE_AGREEMENT_CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'number status actions',
    'agreement agreement agreement',
    'schedule schedule schedule',
    'next next next',
    'client client client',
    'price price price',
  ],
  footer: ['price'],
};

export const SERVICE_AGREEMENT_MIN_TABLE_WIDTH = 1180;

const STATUS_TONES = { active: 'green', completed: 'blue', draft: 'grey', cancelled: 'red', paused: 'amber' };

const titleCase = (value) =>
  String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// "Every 3 months on the 1st" from the stored recurrence rule.
const repeatText = (rrule) => {
  if (!rrule) return null;
  try {
    const text = rrulestr(rrule).toText();
    return text.charAt(0).toUpperCase() + text.slice(1);
  } catch (error) {
    return null;
  }
};

// "10:21am" → "10:21 AM"
const clock = (value) => (value ? String(value).replace(/\s*(am|pm)$/i, (m, p) => ` ${p.toUpperCase()}`) : null);

const dayLabel = (date) => {
  const time = currentTime(date);
  return time.format(time.isSame(currentTime(), 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
};

const FILTERS = { client_id: 'client', location_id: 'location', service_type_id: 'service type' };

@inject('store', 'serviceAgreementStore', 'viewStore') @observer
class ServiceAgreementsTable extends Component {

  onPaginationChange(newPage) {
    let {
      viewStore: {
        serviceAgreementSearch
      }
    } = this.props;
    this.props.viewStore.serviceAgreementSearch = {
      ...serviceAgreementSearch,
      page: newPage
    };
    this.props.viewStore.serviceAgreements();
  }

  clearFilters() {
    const { viewStore } = this.props;
    viewStore.serviceAgreementSearch = {
      ...viewStore.serviceAgreementSearch,
      term: '',
      where: { client_id: '', location_id: '', service_type_id: [] },
      page: 1,
    };
    viewStore.bumpListFilters();
    viewStore.serviceAgreements();
  }

  render() {
    let {
      viewStore,
      serviceAgreementStore: {
        serviceAgreements: {
          objects,
          meta
        }
      }
    } = this.props;
    const search = viewStore.serviceAgreementSearch;

    if (objects.length === 0) {
      const term = search.term && search.term !== '*' ? search.term : null;
      return (
        <DataList.Empty
          noun="service agreements"
          filters={DataList.activeFilters(search.where, FILTERS)}
          term={term}
          onClearFilters={() => this.clearFilters()}
          message={term || DataList.activeFilters(search.where, FILTERS).length > 0
            ? undefined
            : 'Recurring work you set up for clients, like maintenance plans, shows up here.'}
        />
      );
    }

    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={SERVICE_AGREEMENT_COLUMNS}
          cards={SERVICE_AGREEMENT_CARDS}
          minTableWidth={SERVICE_AGREEMENT_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="service agreements"
        >
          {objects.map((serviceAgreement) => {
            const attributes = serviceAgreement.attributes;
            const serviceType = attributes.service_type || {};
            const client = attributes.client || {};
            const location = attributes.location || {};
            const nextVisit = (attributes.jobs || [])[0];
            const start = clock(attributes.formatted_start_time);
            const end = clock(attributes.formatted_end_time);
            const clientName = client.legal_name || client.name;
            const status = attributes.status;
            return (
              <DataList.Row
                key={attributes.id}
                accent={serviceType.name ? accentFor(serviceType.color) : undefined}
              >
                <DataList.Cell area="number">
                  <div className="schedule-number">
                    <DataList.RowLink
                      href={`/dashboard/service_agreements/${attributes.id}/show`}
                      label={`Service agreement ${attributes.number}: ${attributes.title || ''}`}
                    >
                      {attributes.number}
                    </DataList.RowLink>
                    {serviceType.name && (
                      <div className="schedule-number__type">
                        <span
                          className="schedule-number__dot"
                          style={{ backgroundColor: accentFor(serviceType.color) }}
                        />
                        <span className="dl-truncate">{serviceType.name}</span>
                      </div>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="agreement">
                  <DataList.Lines
                    primary={attributes.title || 'Untitled agreement'}
                    secondary={attributes.reason_for_call}
                  />
                </DataList.Cell>
                <DataList.Cell area="schedule">
                  <DataList.Lines
                    primary={repeatText(attributes.rrule) || 'No repeat set'}
                    strong={false}
                    secondary={start ? `${start}${end ? ` – ${end}` : ''}` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="next">
                  {nextVisit ? (
                    <DataList.Lines
                      primary={<><DataList.CardOnly>Next visit </DataList.CardOnly>{dayLabel(nextVisit.start_date)}</>}
                      strong={false}
                      secondary={nextVisit.event_number ? `#${nextVisit.event_number}` : null}
                    />
                  ) : (
                    <span className="dl-muted">No upcoming visits</span>
                  )}
                </DataList.Cell>
                <DataList.Cell area="client">
                  {clientName ? (
                    <DataList.Lines
                      primary={clientName}
                      href={attributes.client_id
                        ? `/dashboard/clients/${attributes.client_id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`
                        : undefined}
                      secondary={location.full_address}
                    />
                  ) : (
                    <DataList.Lines primary="No client" strong={false} secondary={location.full_address} />
                  )}
                </DataList.Cell>
                <DataList.Cell area="price">
                  {attributes.price != null ? (
                    <span className="dl-lines__primary">
                      <DataList.CardOnly>Price </DataList.CardOnly>
                      {currencyFormattedAmount(attributes.price)}
                    </span>
                  ) : (
                    <span className="dl-muted">No price</span>
                  )}
                </DataList.Cell>
                <DataList.Cell area="status">
                  {status && <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>{titleCase(status)}</DataList.Pill>}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[{
                      icon: 'edit',
                      label: 'Edit service agreement',
                      onClick: () => viewStore.serviceAgreementShow({ uuid: attributes.id }),
                    }]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
      </React.Fragment>
    )
  }

};

export default ServiceAgreementsTable;
